import { Lang, lang } from '@src/models/signals'
import { Languages } from '@src/models/types'
import { computed, signal } from '@lit-labs/preact-signals'
import { isMac } from '@utils/metaKey'
import { getText, type TextTable, type TextGroup } from '@shared/i18n/i18n'
import { setDefaultOptions } from 'date-fns'
import { enUS, es, fr, de, it, pt, nl, pl, cs, hu } from 'date-fns/locale'

export type { TextTable, TextGroup }

// date-fns has no global locale by default, so every `format()` call site renders English
// month/weekday names regardless of the selected language (#1584). Map our languages to their
// date-fns locale and apply it via `setDefaultOptions` whenever the language changes, fixing all
// call sites at once. (The calendar widget is localized separately via calendarLocale.)
const dateFnsLocales: Record<Lang, typeof enUS> = { en: enUS, es, fr, de, it, pt, nl, pl, cs, hu }

// Language configuration data
interface LanguageConfig {
	direction: 'ltr' | 'rtl'
	fontSizeAdjustment: boolean
}

// Language configuration map
const languageConfigs: Record<Lang, LanguageConfig> = {
	en: { direction: 'ltr', fontSizeAdjustment: false },
	es: { direction: 'ltr', fontSizeAdjustment: false },
	fr: { direction: 'ltr', fontSizeAdjustment: false },
	de: { direction: 'ltr', fontSizeAdjustment: false },
	it: { direction: 'ltr', fontSizeAdjustment: false },
	pt: { direction: 'ltr', fontSizeAdjustment: false },
	nl: { direction: 'ltr', fontSizeAdjustment: false },
	pl: { direction: 'ltr', fontSizeAdjustment: false },
	cs: { direction: 'ltr', fontSizeAdjustment: false },
	hu: { direction: 'ltr', fontSizeAdjustment: false },
}

// Create signals for language state
export const isRTL = computed(() => (languageConfigs[lang.value]?.direction || 'ltr') === 'rtl')
export const needsFontAdjustment = computed(() => languageConfigs[lang.value]?.fontSizeAdjustment || false)

// Cookie name constant
export const LANG_COOKIE_NAME = 'bb-lang'

export let urlLang: Lang | undefined = (() => {
	const param = new URLSearchParams(window.location.search).get('lang')
	return param && param in Languages ? (param as Lang) : undefined
})()

export const clearUrlLang = () => {
	urlLang = undefined
	const url = new URL(window.location.href)
	if (url.searchParams.has('lang')) {
		url.searchParams.delete('lang')
		history.replaceState(history.state, '', url)
	}
}

// Apply language settings based on signals
export const applyLanguageSettings = (): void => {
	const htmlElement = document.documentElement

	// Set language attribute
	htmlElement.lang = lang.value

	// Localize every date-fns format() call site at once (#1584). Pin weekStartsOn to Sunday (the
	// date-fns default before any locale was set) so this only affects month/weekday *names* — a
	// locale's own weekStartsOn (Monday for fr/es) would otherwise silently shift week-boundary math
	// in callers that don't pass it explicitly (e.g. bb-repeat recurrence), changing behavior by UI
	// language. Callers that care about the week start (bb-calendar) already pass it themselves.
	setDefaultOptions({ locale: dateFnsLocales[lang.value] ?? enUS, weekStartsOn: 0 })

	// Set direction
	htmlElement.dir = isRTL.value ? 'rtl' : 'ltr'

	// Apply RTL class if needed (useful for custom styling)
	if (isRTL.value) {
		document.body.classList.add('rtl')
	} else {
		document.body.classList.remove('rtl')
	}

	// Apply font adjustment class if needed
	if (needsFontAdjustment.value) {
		document.body.classList.add('cjk-text')
	} else {
		document.body.classList.remove('cjk-text')
	}
}

// Subscribe to language changes to apply settings
lang.subscribe(() => {
	applyLanguageSettings()
})

class I18n {
	// Signal, not a plain field: components read it via `this.t()` during render, so
	// when labels finish loading (`texts.value = …`) every SignalWatcher that rendered
	// before they landed re-renders instead of staying stuck on the `group.key` fallback.
	// Previously reactivity piggybacked on the `lang` signal changing, which is skipped at
	// boot when `lang` is already 'en' (no-op write) — that was the #1513 race.
	texts = signal<TextTable>({})

	load(langKey: Lang, cb?: () => void) {
		const whenDone = (file: { default: TextTable }) => {
			this.updateText(file.default)

			lang.value = langKey

			if (cb) {
				cb()
			}
		}
		import(`@shared/i18n/${langKey || 'en'}/labels.json`).then(whenDone).catch(() => {
			// fallback to english
			import(`@shared/i18n/en/labels.json`).then(whenDone)
		})
	}

	updateText(newText: TextTable) {
		this.texts.value = newText
	}

	getTextGroup(group: string) {
		return this.texts.value[group]
	}

	getText(group: string, key: string, defaultText?: string): string {
		const textValue = getText(this.texts.value, group, key, defaultText)
		//replacing the mac key symbol with the ctrl key symbol
		if (textValue.indexOf('⌘') === -1 || isMac) {
			return textValue
		} else {
			return textValue.replace('⌘', 'Ctrl')
		}
	}
}

export const i18n = new I18n()
